import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Coordinates the two right-docked panels (agent chat and patient notes) so
// only one is ever expanded, and carries the notes panel's target patient.

const AGENT_OPEN_KEY = 'clinic-agent-open';

export type SidePanel = 'agent' | 'notes' | null;

export type NotesPatient = {
  id: number; // UserProfile id (same id as /clinic/patient/{id}/ routes)
  name: string;
};

export type NotesTab = 'all' | 'pinned';

export interface SidePanelContextValue {
  activePanel: SidePanel;
  notesPatient: NotesPatient | null;
  // Set when a launch surface asks for a specific tab (e.g. the pinned-notes
  // strip opens "Fijadas"). seq bumps on every request so the panel applies it
  // even if the tab value repeats.
  notesTabRequest: { tab: NotesTab; seq: number } | null;
  openNotesForPatient: (patient: NotesPatient, initialTab?: NotesTab) => void;
  closeNotes: () => void;
  openAgent: () => void;
  closeAgent: () => void;
}

const SidePanelContext = createContext<SidePanelContextValue | undefined>(undefined);

export const SidePanelProvider = ({ children }: { children: ReactNode }) => {
  const [activePanel, setActivePanel] = useState<SidePanel>(() => {
    // Notes never starts open (its target patient isn't persisted), so the
    // initial state only restores the agent chat, keeping its historical
    // default: stored preference, else open on desktop.
    const stored = localStorage.getItem(AGENT_OPEN_KEY);
    if (stored != null) return stored === 'true' ? 'agent' : null;
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;
    return isMobile ? null : 'agent';
  });
  const [notesPatient, setNotesPatient] = useState<NotesPatient | null>(null);
  const [notesTabRequest, setNotesTabRequest] = useState<{ tab: NotesTab; seq: number } | null>(
    null,
  );

  useEffect(() => {
    localStorage.setItem(AGENT_OPEN_KEY, String(activePanel === 'agent'));
  }, [activePanel]);

  const openNotesForPatient = useCallback((patient: NotesPatient, initialTab?: NotesTab) => {
    setNotesPatient(patient);
    if (initialTab) setNotesTabRequest((prev) => ({ tab: initialTab, seq: (prev?.seq ?? 0) + 1 }));
    setActivePanel('notes');
  }, []);

  const closeNotes = useCallback(() => {
    setActivePanel((prev) => (prev === 'notes' ? null : prev));
  }, []);

  const openAgent = useCallback(() => {
    setActivePanel('agent');
  }, []);

  const closeAgent = useCallback(() => {
    setActivePanel((prev) => (prev === 'agent' ? null : prev));
  }, []);

  const value = useMemo(
    () => ({
      activePanel,
      notesPatient,
      notesTabRequest,
      openNotesForPatient,
      closeNotes,
      openAgent,
      closeAgent,
    }),
    [activePanel, notesPatient, notesTabRequest, openNotesForPatient, closeNotes, openAgent, closeAgent],
  );

  return <SidePanelContext.Provider value={value}>{children}</SidePanelContext.Provider>;
};

export const useSidePanel = (): SidePanelContextValue | null =>
  useContext(SidePanelContext) ?? null;
